import { TaskOutcomeDataSchema } from "@erp/shared-types";
import { z } from "zod";
import {
  AP_EXCEPTION_OWNER_TYPES,
  SystemEvent,
  SystemEntity,
  credentialRejectionSurfaceSchema,
} from "@erp/shared-types";
import { entityOfType, SystemEntitySchema } from "../utils/system-entities";
import {
  REMITTANCE_EXTRACTION_MODES,
  RemittanceDocSchema,
  remittanceProvenanceSchema,
} from "../steps/accounts-receivable/remittances/remittance-schemas";

// ============================================================================
// Event Schemas
// ============================================================================

/**
 * Schema for EMAIL_INGESTED event
 * Emitted after an email is ingested and OCR has been performed
 */
export const EmailIngestedEventSchema = z.object({
  inboundEmailId: z.uuid(),
  organizationId: z.uuid(),
  to: z.array(z.string()).optional(),
  cc: z.array(z.string()).optional(),
  from: z.string().optional(),
  subject: z.string().optional(),
  messageId: z.string(),
  conversationId: z.string().optional(),
  documentIds: z.array(z.uuid()),
  mimeTypes: z.array(z.string()),
  attachmentCount: z.number(),
  classificationHints: z
    .array(z.string())
    .optional()
    .describe(
      "Hints about what workflows might match (e.g., 'ap_invoice', 'purchase_order')",
    ),
  routerHints: z
    .object({
      ap: z.boolean().optional(),
      po: z.boolean().optional(),
      quoteSheet: z.boolean().optional(),
    })
    .optional(),
});

export type EmailIngestedEvent = z.infer<typeof EmailIngestedEventSchema>;

/**
 * Schema for EMAIL_ROUTED_EMAIL_RULE event
 * Emitted after router determines email should be processed as email rule
 */
export const EmailRoutedEmailRuleEventSchema = z.object({
  emailRuleOptionId: z.uuid().optional(),
  emailRuleId: z.uuid().optional(),
  emailRuleIds: z.array(z.uuid()).optional(),
  inboundEmail: entityOfType(SystemEntity.INBOUND_EMAIL),
  /** Documents (attachments + email body) associated with the inbound email */
  documents: z.array(entityOfType(SystemEntity.CORE_DOCUMENT)).optional(),
  /**
   * Set by a user-initiated re-run that asked for a fresh extraction: the run
   * starts in `runMode: "rerun"`, so steps that reuse completed results
   * (document OCR) redo the work instead of replaying the cached result.
   */
  forceFreshExtraction: z.boolean().optional(),
});

export type EmailRoutedEmailRuleEvent = z.infer<
  typeof EmailRoutedEmailRuleEventSchema
>;
/**
 * Schema for EMAIL_ROUTED_AR_REMITTANCE event
 * Emitted after router determines email should be processed as AR remittance
 */
export const EmailRoutedARRemittanceEventSchema = z.object({
  inboundEmail: entityOfType(SystemEntity.INBOUND_EMAIL),
});

export type EmailRoutedARRemittanceEvent = z.infer<
  typeof EmailRoutedARRemittanceEventSchema
>;

/**
 * Schema for AP_INVOICE_DRAFT_CREATED event.
 * Emitted by `create_ap_invoice_draft` when a new DRAFT invoice is persisted
 * from an extraction result (not on reruns or dedup/thread matches that
 * resolve to an existing invoice).
 *
 * `referenceIds` is on the event (not only on the invoice row) so a handling
 * workflow triggered here can wire Famous PO/SO/TO/load fetch steps the same
 * way the old monolith wired them off `create_ap_invoice_draft`. Prefer
 * `get_ap_invoice` when you need a live reload (e.g. snooze reruns after a
 * human edited refs). Empty when the extraction found no references.
 */
export const APInvoiceDraftCreatedEventSchema = z.object({
  apInvoice: entityOfType(SystemEntity.AP_INVOICE),
  /** Flat ID for workflow edge wiring (duplicates apInvoice.entityId). */
  apInvoiceId: z.uuid(),
  organizationId: z.uuid(),
  /** Matched supplier, or null when the draft was created without one. */
  supplierId: z.uuid().nullable(),
  /** Extracted invoice number, or null when none was found. */
  invoiceNumber: z.string().nullable(),
  /** The extraction result the draft was built from. */
  extractionResultId: z.uuid(),
  /**
   * PO / SO / TO / load numbers stored on the invoice. Empty when none.
   * Same bag of tokens the Famous fetch steps already spray at Oracle.
   */
  referenceIds: z.array(z.string()),
});

export type APInvoiceDraftCreatedEvent = z.infer<
  typeof APInvoiceDraftCreatedEventSchema
>;

export const APInvoiceAcceptedEventSchema = z.object({
  apInvoice: entityOfType(SystemEntity.AP_INVOICE),
});

export type APInvoiceAcceptedEvent = z.infer<
  typeof APInvoiceAcceptedEventSchema
>;

/**
 * Schema for AP_INVOICE_EXCEPTIONS_EVALUATED.
 * Emitted after an exception-evaluation pass that leaves exceptions open
 * (`outcome = "open"`) or resolves the last open ones (`outcome = "cleared"`).
 * A pass that finds nothing and changes nothing does not emit. The dry-run
 * test does not emit. `outcome` and `ownerType` are strings so an event
 * trigger can payload-filter on them.
 */
export const APInvoiceExceptionsEvaluatedEventSchema = z.object({
  apInvoice: entityOfType(SystemEntity.AP_INVOICE),
  /** Flat ID for workflow edge wiring (duplicates apInvoice.entityId). */
  apInvoiceId: z.uuid(),
  organizationId: z.uuid(),
  exceptionRuleSetId: z.uuid(),
  /** Match-node task wired into evaluation, when one was. */
  taskId: z.uuid().nullable(),
  /** Workflow run that evaluated, or null for a manual re-evaluate. */
  sourceWorkflowRunId: z.uuid().nullable(),
  outcome: z.enum(["open", "cleared"]),
  hasOpenExceptions: z.boolean(),
  openedCount: z.number().int().nonnegative(),
  resolvedCount: z.number().int().nonnegative(),
  exceptions: z.array(
    z.object({
      exceptionTypeOptionId: z.uuid(),
      name: z.string(),
      reasoning: z.string(),
    }),
  ),
  ownerType: z.enum(AP_EXCEPTION_OWNER_TYPES).nullable(),
  ownerId: z.uuid().nullable(),
});

export type APInvoiceExceptionsEvaluatedEvent = z.infer<
  typeof APInvoiceExceptionsEvaluatedEventSchema
>;

/**
 * Schema for PAYMENT_APPROVED event.
 * Emitted when an AP payment collects its last required approval and moves
 * to `approved`. `createdByEmail` wires straight into Send Email's `to`.
 */
export const PaymentApprovedEventSchema = z.object({
  payment: entityOfType(SystemEntity.PAYMENT),
  /** Flat ID for workflow edge wiring (duplicates payment.entityId). */
  paymentId: z.uuid(),
  organizationId: z.uuid(),
  /** User who created the payment; null for system-created or older payments. */
  createdByUserId: z.uuid().nullable(),
  /** Email of the payment's creator; null when there is no creator. */
  createdByEmail: z.string().nullable(),
  /** User whose approval completed the threshold. */
  approvedByUserId: z.uuid(),
  amount: z.string(),
  currency: z.string(),
  /** YYYY-MM-DD */
  paymentDate: z.string(),
  method: z.string(),
  /** Payee name captured at approval, when available. */
  recipientName: z.string().nullable(),
});

export type PaymentApprovedEvent = z.infer<typeof PaymentApprovedEventSchema>;

/**
 * Schema for SALES_ORDER_APPROVED event
 * Emitted when a sales order draft is approved and synced to ERP
 */
export const SalesOrderApprovedEventSchema = z.object({
  salesOrder: entityOfType(SystemEntity.AR_SALES_ORDER),
  salesOrderDraft: entityOfType(SystemEntity.AR_STAGED_SALES_ORDER),
  organizationId: z.uuid(),
  /** Flat ID for workflow edge wiring (duplicates salesOrder.entityId). */
  salesOrderId: z.uuid(),
  /** Flat ID for workflow edge wiring (duplicates salesOrderDraft.entityId). */
  salesOrderDraftId: z.uuid(),
  /** Customer ID for resolving customer-scoped watchers in notification workflows. */
  customerId: z.uuid().nullable(),
  /** Serialised approval issues (may be empty when no issues). */
  issues: z.array(
    z.object({
      lineItemIndex: z.number(),
      issue: z.object({
        type: z.string(),
        details: z.string().optional(),
        excludedFromOrder: z.boolean(),
      }),
    }),
  ),
});

export type SalesOrderApprovedEvent = z.infer<
  typeof SalesOrderApprovedEventSchema
>;

/**
 * Schema for DRAFT_PURCHASE_ORDER_CREATED event (procurement PO approval).
 * Emitted when buyer Confirm stages a draft purchase order held for approval.
 * `divisionId` routes to the correct per-division approval workflow.
 */
export const DraftPurchaseOrderCreatedEventSchema = z.object({
  draftPurchaseOrderId: z.uuid(),
  organizationId: z.uuid(),
  /** Division of the PO's ship-to warehouse; the per-division trigger filters on it. */
  divisionId: z.uuid().nullable(),
});

export type DraftPurchaseOrderCreatedEvent = z.infer<
  typeof DraftPurchaseOrderCreatedEventSchema
>;

/**
 * Schema for TASK_CREATED event.
 * Emitted from the task service layer whenever a task is created (oRPC
 * handlers, createTask/generateTask/resolve_task/create_subtask workflow
 * steps, getOrCreateByAnchor). The type/anchor fields are optional for
 * backward compatibility with legacy emitters (e.g. the AR scan handlers)
 * that publish only the task reference.
 */
export const TaskCreatedEventSchema = z.object({
  task: entityOfType(SystemEntity.TASK),
  organizationId: z.uuid(),
  sourceEntity: SystemEntitySchema.optional(),
  /** Flat ID for workflow edge wiring (duplicates task.entityId). */
  taskId: z.uuid().optional(),
  /** Org-scoped task type key, when the task is typed. */
  taskTypeKey: z.string().nullable().optional(),
  /** The business entity the task is anchored to, when anchored. */
  anchorEntity: SystemEntitySchema.optional(),
  /** The task's status at creation time (normally "pending"). */
  status: z.string().optional(),
  /**
   * The task's parent when it is a subtask, else the task itself — the id a
   * workflow wires as `parentTaskId` so a run triggered by a parent's creation
   * and one triggered by a subtask's status change both attach stage subtasks
   * to the same chain.
   */
  chainTaskId: z.uuid().optional(),
});

export type TaskCreatedEvent = z.infer<typeof TaskCreatedEventSchema>;

/**
 * Schema for TASK_STATUS_CHANGED event.
 * Emitted from the task service layer on every task status transition —
 * exactly once per transition, regardless of which path drove it (oRPC
 * handlers, workflow steps, the agent task loop, escalation). Terminal
 * transitions additionally emit TASK_COMPLETED.
 */
export const TaskStatusChangedEventSchema = z.object({
  task: entityOfType(SystemEntity.TASK),
  /** Flat ID for workflow edge wiring (duplicates task.entityId). */
  taskId: z.uuid(),
  organizationId: z.uuid(),
  /** Org-scoped task type key, null for untyped tasks. */
  taskTypeKey: z.string().nullable(),
  /** The business entity the task is anchored to, when anchored. */
  anchorEntity: SystemEntitySchema.optional(),
  previousStatus: z.string(),
  status: z.string(),
  /** Terminal outcome (validated against the type vocabulary), else null. */
  outcome: z.string().nullable(),
  outcomeReason: z.string().nullable(),
  /** The task's parent when it is a subtask, else the task itself (see TASK_CREATED). */
  chainTaskId: z.uuid().optional(),
  outcomeData: TaskOutcomeDataSchema.nullable().optional(),
  outcomeContractFingerprint: z.string().nullable().optional(),
});

export type TaskStatusChangedEvent = z.infer<
  typeof TaskStatusChangedEventSchema
>;

/**
 * Schema for CREDENTIAL_REJECTED event.
 * Emitted after handling a reported credential rejection and revoking the current run's leases.
 * `pausedResourceIds` names resources paused by an opted-in credential policy; otherwise empty.
 * Names and ids only, never secret values
 * or the operator's failure prose.
 */
export const CredentialRejectedEventSchema = z.object({
  organizationId: z.uuid(),
  /** The rejected credential, or null when the operator could not resolve one. */
  credentialId: z.uuid().nullable(),
  /** Non-secret label of the rejected credential. */
  credentialName: z.string(),
  surface: credentialRejectionSurfaceSchema,
  /** The agent run that hit the rejection (not necessarily the lease holder). */
  agentRunId: z.uuid(),
  /** Leaseable resources deactivated by this rejection. */
  pausedResourceIds: z.array(z.uuid()),
});

export type CredentialRejectedEvent = z.infer<
  typeof CredentialRejectedEventSchema
>;

/**
 * Schema for REBATE_CSV_TRANSFORM_STARTED event
 * Only passes sessionId — the auto-transform handler loads everything else from the session.
 */
export const RebateCsvTransformStartedEventSchema = z.object({
  rebateSessionId: z.string().uuid(),
});

export type RebateCsvTransformStartedEvent = z.infer<
  typeof RebateCsvTransformStartedEventSchema
>;

/**
 * Shared input shape for SPA change-request workflow steps (fetch vendor SPA,
 * fetch AS400 current loads, calculate price changes). Carries the SPA (quote)
 * number and vendor key so each step can route to the correct vendor portal
 * without re-fetching. The workflow is triggered by `SPA_REQUEST_SUBMITTED`,
 * whose payload supplies these fields to the trigger's output sockets.
 */
export const SpaWorkflowInputSchema = z.object({
  spaNumber: z.string().min(1),
  vendor: z.string().min(1),
});

export type SpaWorkflowInput = z.infer<typeof SpaWorkflowInputSchema>;

/**
 * Schema for SPA_REQUEST_SUBMITTED event
 * Emitted when a SpaRequest entity is created in the DB (Module 1 create handler).
 * This is the event the Module 9 DAG listens on to start the full SPA pipeline.
 */
export const SpaRequestSubmittedEventSchema = z.object({
  spaRequestId: z.string().uuid(),
  spaNumber: z.string().min(1),
  vendor: z.string().min(1),
  organizationId: z.string().uuid(),
});

export type SpaRequestSubmittedEvent = z.infer<
  typeof SpaRequestSubmittedEventSchema
>;

/**
 * Schema for APPROVAL_NEEDED event
 * Emitted when a staged PO requires approval — either discount-level (unapproved pricing)
 * or order-level (e.g., total exceeds threshold). Triggers the notification workflow.
 */
export const ApprovalNeededEventSchema = z.object({
  organizationId: z.uuid(),
  /** The staged purchase order requiring approval */
  stagedPurchaseOrderId: z.uuid(),
  /** Customer ID for resolving customer-scoped approvers */
  customerId: z.uuid().nullable(),
  /**
   * The discount approval level number that needs to act next. Set when
   * the next pending action is a discount approval, null otherwise.
   * Exactly one of nextDiscountApprovalLevel / nextOrderApprovalLevel is
   * non-null per event.
   */
  nextDiscountApprovalLevel: z.number().int().positive().nullable(),
  /** The order approval level number that needs to act next, or null. */
  nextOrderApprovalLevel: z.number().int().positive().nullable(),
  /** Idempotency key prefix for email deduplication */
  idempotencyKeyPrefix: z.string(),
  /** User IDs to exclude from notification (e.g., the user who just triggered the recheck) */
  excludeUserIds: z.array(z.string().uuid()).nullable(),
  /**
   * Full order results with issue details for rendering notification email.
   * Same shape as detect_order_issues output.
   */
  orderResults: z.array(
    z.object({
      stagedPurchaseOrderId: z.uuid(),
      customerId: z.uuid().nullable(),
      issues: z.array(
        z.object({
          type: z.string(),
          description: z.string(),
          lineItemIndex: z.number().optional(),
          lineItemDescription: z.string().optional(),
        }),
      ),
      issueTypes: z.array(z.string()),
      orderSummary: z.object({
        purchaseOrderNumber: z.string().nullable(),
        customerName: z.string().nullable(),
        customerNumber: z.string().nullable().optional(),
        orderType: z.string().nullable().optional(),
        shipToLocation: z.string().nullable().optional(),
        orderDate: z.string().nullable().optional(),
        requestDate: z.string().nullable().optional(),
        totalAmount: z.number().nullable(),
        currency: z.string().nullable(),
        lineItemCount: z.number(),
        receivedDate: z.string().nullable(),
      }),
    }),
  ),
});

export type ApprovalNeededEvent = z.infer<typeof ApprovalNeededEventSchema>;

/**
 * Schema for TASK_COMPLETED event (counterpart to TASK_CREATED).
 * Emitted when any task reaches a terminal status. Two emitter families share
 * this event:
 *
 * 1. The generalized task lifecycle (task service layer): every terminal
 *    transition of a real Task record publishes the task reference plus
 *    taskTypeKey / anchorEntity / status / outcome.
 * 2. The OMS v3 event-driven approval gates: decision recording for
 *    (possibly task-less) approvals publishes category + flat subject ids +
 *    decision, which existing subscribers filter on (e.g. OMS v3 workflow-3
 *    filters to category "sales_order"). Lifecycle emissions never set
 *    `category`, so those payload filters keep matching only gate decisions.
 */
export const TaskCompletedEventSchema = z.object({
  /** The formal Task entity, when one exists (omitted for task-less approvals). */
  task: entityOfType(SystemEntity.TASK).optional(),
  taskId: z.uuid().optional(),
  organizationId: z.uuid(),
  /** Task category used by event-trigger payload filters (e.g. "sales_order"). */
  category: z.string().optional(),
  /** The entity the task is about (e.g. the draft sales order). */
  linkedEntity: SystemEntitySchema.optional(),
  /** Flat draft id for workflow edge wiring, when the task is draft-scoped. */
  draftSalesOrderId: z.uuid().optional(),
  /** Flat discount-group id, when the task is a Discounts v2 group approval. */
  discountGroupId: z.uuid().optional(),
  /** Flat draft-PO id, when the task is a procurement PO approval. */
  draftPurchaseOrderId: z.uuid().optional(),
  /** Outcome of the task (e.g. approval decision: approved | rejected). */
  decision: z.string().optional(),
  /** Org-scoped task type key, null for untyped tasks (lifecycle emissions). */
  taskTypeKey: z.string().nullable().optional(),
  /** The business entity the task is anchored to, when anchored. */
  anchorEntity: SystemEntitySchema.optional(),
  /** Terminal status reached: completed | failed | cancelled. */
  status: z.string().optional(),
  /** Terminal outcome recorded on the task (validated against the type vocabulary). */
  outcome: z.string().nullable().optional(),
  outcomeReason: z.string().nullable().optional(),
  outcomeData: TaskOutcomeDataSchema.nullable().optional(),
  outcomeContractFingerprint: z.string().nullable().optional(),
});

export type TaskCompletedEvent = z.infer<typeof TaskCompletedEventSchema>;

/**
 * Schema for DRAFT_SALES_ORDER_ROUTED event (OMS v3, routing org → target org).
 * Cross-org: carries the target organization the routing workflow classified.
 */
export const DraftSalesOrderRoutedEventSchema = z.object({
  draftSalesOrderId: z.uuid(),
  targetOrganizationId: z.uuid(),
  inboundEmailId: z.uuid().optional(),
});

export type DraftSalesOrderRoutedEvent = z.infer<
  typeof DraftSalesOrderRoutedEventSchema
>;

/**
 * Schema for DRAFT_SALES_ORDER_READY event (OMS v3, intra-org wf2 → wf3).
 * Emitted when the target org's extract/line-breakout workflow has populated
 * the draft and it is ready for the approval-chain workflow.
 */
export const DraftSalesOrderReadyEventSchema = z.object({
  draftSalesOrderId: z.uuid(),
});

export type DraftSalesOrderReadyEvent = z.infer<
  typeof DraftSalesOrderReadyEventSchema
>;

/**
 * Schema for DRAFT_SALES_ORDER_UNCLASSIFIED event (OMS v3, routing org).
 * Emitted when classify_organization can't confidently match a target org, so an
 * admin-notification workflow can alert the Gently team to assign it manually.
 */
export const DraftSalesOrderUnclassifiedEventSchema = z.object({
  draftSalesOrderId: z.uuid(),
});

export type DraftSalesOrderUnclassifiedEvent = z.infer<
  typeof DraftSalesOrderUnclassifiedEventSchema
>;

/**
 * Schema for DISCOUNT_GROUP_SUBMITTED event (Discounts v2).
 * Emitted when a discount group (exception price list / bulk batch / discount
 * request) is submitted for approval; triggers the group-approval workflow.
 */
export const DiscountGroupSubmittedEventSchema = z.object({
  organizationId: z.uuid(),
  discountGroupId: z.uuid(),
});

export type DiscountGroupSubmittedEvent = z.infer<
  typeof DiscountGroupSubmittedEventSchema
>;

/**
 * Schema for DISCOUNT_REQUEST_DOCUMENT_ROUTED event (Discounts v2, routing org
 * → target org). Cross-org: the routing workflow classified an inbound email's
 * document(s) as a Discount Request and matched the selling org. Carries the
 * inbound email so the target org's discount-request-ingestion workflow can
 * extract its discount request attachment(s) and create the discount group
 * there.
 */
export const DiscountRequestDocumentRoutedEventSchema = z.object({
  /** Plain id, for uuid inputs (e.g. custom canvas wiring). */
  inboundEmailId: z.uuid(),
  /**
   * The same email as an entity reference — this is what Extract Document
   * Content (V2)'s `inboundEmail` input expects, so the trigger→extract edge
   * type-checks on the canvas (uuid → entity_reference is rejected there,
   * even though the runtime schema would coerce a bare uuid).
   */
  inboundEmail: entityOfType(SystemEntity.INBOUND_EMAIL),
  targetOrganizationId: z.uuid(),
});

export type DiscountRequestDocumentRoutedEvent = z.infer<
  typeof DiscountRequestDocumentRoutedEventSchema
>;

/**
 * Schema for AR_REMITTANCE_DOCUMENT_SUBMITTED. The intake contract of
 * `RemittanceIntakeService.submit()`: one remittance document from a non-email
 * source and where it came from. submit() starts the document ingestion
 * workflow directly with this as the trigger's event data; nothing publishes
 * it on the event bus.
 */
export const ARRemittanceDocumentSubmittedEventSchema = z.object({
  /** The remittance document; kept as evidence even when pre-normalized. */
  document: entityOfType(SystemEntity.CORE_DOCUMENT),
  /** Channel, raw reference, received time and source ids of the intake. */
  provenance: remittanceProvenanceSchema,
  /** `extract` runs extraction; `pre_normalized` skips it. */
  extractionMode: z.enum(REMITTANCE_EXTRACTION_MODES),
  /** Set exactly when extractionMode is `pre_normalized`. */
  preNormalized: RemittanceDocSchema.optional(),
});

export type ARRemittanceDocumentSubmittedEvent = z.infer<
  typeof ARRemittanceDocumentSubmittedEventSchema
>;

/**
 * Schema for AR_DEPOSIT_FILE_SUBMITTED: one file dropped into a check deposit.
 * The deposit service starts the org's deposit file workflow directly with
 * this as the trigger's event data; nothing publishes it on the event bus.
 */
export const ARDepositFileSubmittedEventSchema = z.object({
  /** The uploaded file: a check, a remittance, or a check with its stub. */
  document: entityOfType(SystemEntity.CORE_DOCUMENT),
  /** The check deposit the file was dropped into. */
  depositBatchId: z.uuid(),
  /** The deposit's row for this file, which the workflow fills in. */
  depositBatchItemId: z.uuid(),
});

export type ARDepositFileSubmittedEvent = z.infer<
  typeof ARDepositFileSubmittedEventSchema
>;

/**
 * Schema for AR_REMITTANCE_PORTAL_FETCH_REQUESTED: fetch a customer's
 * remittances from its portal. Each "AR Remittance Portal Fetch" workflow
 * serves one portal and filters this event on `customerId` in its trigger.
 */
export const ARRemittancePortalFetchRequestedEventSchema = z.object({
  /** The customer whose portal to fetch from. */
  customerId: z.uuid(),
  /** The cash receipt the fetch is for; its remittance links to it. */
  bankTransactionId: z.uuid().optional(),
  /** The portal-notice email that prompted the fetch, if any. */
  inboundEmailId: z.uuid().optional(),
  /**
   * What to download, for the browser operator's prompt: the one payment a
   * receipt fetch is for, or the recent remittances for a general fetch.
   */
  paymentToFind: z.string(),
});

export type ARRemittancePortalFetchRequestedEvent = z.infer<
  typeof ARRemittancePortalFetchRequestedEventSchema
>;

/**
 * Schema for SLACK_WEBHOOK_RECEIVED event
 * Emitted by the Slack webhook receiver for Events API callbacks,
 * interactivity payloads (button clicks, modal submissions), and slash
 * commands. Common identifiers are flattened for easy wiring in workflow
 * steps; the full raw Slack payload is preserved in `payload`.
 */
export const SlackWebhookReceivedEventSchema = z.object({
  /** Which Slack delivery mechanism produced this event */
  kind: z.enum(["event", "interaction", "command"]),
  // The flattened fields below are ALWAYS present — the receiver defaults
  // fields that don't apply to a delivery kind to "". Event-trigger output
  // sockets are required, and a missing value would leave downstream steps
  // (e.g. a Decision on `command`) permanently pending.
  /** Slack workspace (team) id, e.g. T08DR30P6KA */
  teamId: z.string(),
  /** Slack user id of the actor, e.g. U012AB3CD */
  slackUserId: z.string(),
  /** Slack channel / conversation id the activity occurred in */
  channelId: z.string(),
  /** Inner event type for Events API callbacks (e.g. "message"); "" otherwise */
  eventType: z.string(),
  /** Message text for message events, or command text for slash commands */
  text: z.string(),
  /** action_id of the clicked block element for interactions; "" otherwise */
  actionId: z.string(),
  /** value of the clicked block element for interactions; "" otherwise */
  actionValue: z.string(),
  /** Slash command name including the leading slash (e.g. "/register"); "" otherwise */
  command: z.string(),
  /** Short-lived URL for responding to interactions / slash commands; "" otherwise */
  responseUrl: z.string(),
  /** Short-lived trigger id usable for views.open within 3 seconds; "" otherwise */
  triggerId: z.string(),
  /** Full raw Slack payload as received (after JSON / form decoding) */
  payload: z.record(z.string(), z.unknown()),
});

export type SlackWebhookReceivedEvent = z.infer<
  typeof SlackWebhookReceivedEventSchema
>;

/**
 * Schema for WORKBOOK_RUN_COMPLETED: a person's Workbook Run finished,
 * completed or failed. Filter a trigger to one Workbook with a payload filter
 * on `workbookId`.
 */
export const WorkbookRunCompletedEventSchema = z.object({
  organizationId: z.uuid(),
  workbookId: z.uuid(),
  workbookName: z.string(),
  workbookRunId: z.uuid(),
  status: z.enum(["completed", "failed"]),
  /** The Run failure, when it failed. */
  error: z.string().nullable(),
  mode: z.enum(["entire", "up-to", "from", "only"]),
  /** The Revision the Run executed. */
  revision: z.number().int(),
  triggeredByUserId: z.uuid(),
  /** Each Worksheet the Run computed, with its Result's size. */
  results: z.array(
    z.object({
      sheetId: z.uuid(),
      name: z.string(),
      rowCount: z.number().int(),
      errorCount: z.number().int(),
    }),
  ),
});

export type WorkbookRunCompletedEvent = z.infer<
  typeof WorkbookRunCompletedEventSchema
>;

// Map event types to their data types (pre-computed to avoid deep instantiation)
export type SystemEventData = {
  [SystemEvent.EMAIL_INGESTED]: EmailIngestedEvent;
  [SystemEvent.EMAIL_ROUTED_EMAIL_RULE]: EmailRoutedEmailRuleEvent;
  [SystemEvent.EMAIL_ROUTED_AR_REMITTANCE]: EmailRoutedARRemittanceEvent;
  [SystemEvent.AP_INVOICE_DRAFT_CREATED]: APInvoiceDraftCreatedEvent;
  [SystemEvent.AP_INVOICE_ACCEPTED]: APInvoiceAcceptedEvent;
  [SystemEvent.AP_INVOICE_EXCEPTIONS_EVALUATED]: APInvoiceExceptionsEvaluatedEvent;
  [SystemEvent.PAYMENT_APPROVED]: PaymentApprovedEvent;
  [SystemEvent.SALES_ORDER_APPROVED]: SalesOrderApprovedEvent;
  [SystemEvent.DRAFT_PURCHASE_ORDER_CREATED]: DraftPurchaseOrderCreatedEvent;
  [SystemEvent.TASK_CREATED]: TaskCreatedEvent;
  [SystemEvent.TASK_STATUS_CHANGED]: TaskStatusChangedEvent;
  [SystemEvent.TASK_COMPLETED]: TaskCompletedEvent;
  [SystemEvent.CREDENTIAL_REJECTED]: CredentialRejectedEvent;
  [SystemEvent.REBATE_CSV_TRANSFORM_STARTED]: RebateCsvTransformStartedEvent;
  [SystemEvent.SPA_REQUEST_SUBMITTED]: SpaRequestSubmittedEvent;
  [SystemEvent.APPROVAL_NEEDED]: ApprovalNeededEvent;
  [SystemEvent.DRAFT_SALES_ORDER_ROUTED]: DraftSalesOrderRoutedEvent;
  [SystemEvent.DRAFT_SALES_ORDER_READY]: DraftSalesOrderReadyEvent;
  [SystemEvent.DRAFT_SALES_ORDER_UNCLASSIFIED]: DraftSalesOrderUnclassifiedEvent;
  [SystemEvent.DISCOUNT_GROUP_SUBMITTED]: DiscountGroupSubmittedEvent;
  [SystemEvent.DISCOUNT_REQUEST_DOCUMENT_ROUTED]: DiscountRequestDocumentRoutedEvent;
  [SystemEvent.AR_REMITTANCE_DOCUMENT_SUBMITTED]: ARRemittanceDocumentSubmittedEvent;
  [SystemEvent.AR_REMITTANCE_PORTAL_FETCH_REQUESTED]: ARRemittancePortalFetchRequestedEvent;
  [SystemEvent.AR_DEPOSIT_FILE_SUBMITTED]: ARDepositFileSubmittedEvent;
  [SystemEvent.SLACK_WEBHOOK_RECEIVED]: SlackWebhookReceivedEvent;
  [SystemEvent.WORKBOOK_RUN_COMPLETED]: WorkbookRunCompletedEvent;
};

export const SystemEventMap = {
  [SystemEvent.EMAIL_INGESTED]: EmailIngestedEventSchema,
  [SystemEvent.EMAIL_ROUTED_EMAIL_RULE]: EmailRoutedEmailRuleEventSchema,
  [SystemEvent.EMAIL_ROUTED_AR_REMITTANCE]: EmailRoutedARRemittanceEventSchema,
  [SystemEvent.AP_INVOICE_DRAFT_CREATED]: APInvoiceDraftCreatedEventSchema,
  [SystemEvent.AP_INVOICE_ACCEPTED]: APInvoiceAcceptedEventSchema,
  [SystemEvent.AP_INVOICE_EXCEPTIONS_EVALUATED]:
    APInvoiceExceptionsEvaluatedEventSchema,
  [SystemEvent.PAYMENT_APPROVED]: PaymentApprovedEventSchema,
  [SystemEvent.SALES_ORDER_APPROVED]: SalesOrderApprovedEventSchema,
  [SystemEvent.DRAFT_PURCHASE_ORDER_CREATED]:
    DraftPurchaseOrderCreatedEventSchema,
  [SystemEvent.TASK_CREATED]: TaskCreatedEventSchema,
  [SystemEvent.TASK_STATUS_CHANGED]: TaskStatusChangedEventSchema,
  [SystemEvent.TASK_COMPLETED]: TaskCompletedEventSchema,
  [SystemEvent.CREDENTIAL_REJECTED]: CredentialRejectedEventSchema,
  [SystemEvent.REBATE_CSV_TRANSFORM_STARTED]:
    RebateCsvTransformStartedEventSchema,
  [SystemEvent.SPA_REQUEST_SUBMITTED]: SpaRequestSubmittedEventSchema,
  [SystemEvent.APPROVAL_NEEDED]: ApprovalNeededEventSchema,
  [SystemEvent.DRAFT_SALES_ORDER_ROUTED]: DraftSalesOrderRoutedEventSchema,
  [SystemEvent.DRAFT_SALES_ORDER_READY]: DraftSalesOrderReadyEventSchema,
  [SystemEvent.DRAFT_SALES_ORDER_UNCLASSIFIED]:
    DraftSalesOrderUnclassifiedEventSchema,
  [SystemEvent.DISCOUNT_GROUP_SUBMITTED]: DiscountGroupSubmittedEventSchema,
  [SystemEvent.DISCOUNT_REQUEST_DOCUMENT_ROUTED]:
    DiscountRequestDocumentRoutedEventSchema,
  [SystemEvent.AR_REMITTANCE_DOCUMENT_SUBMITTED]:
    ARRemittanceDocumentSubmittedEventSchema,
  [SystemEvent.AR_REMITTANCE_PORTAL_FETCH_REQUESTED]:
    ARRemittancePortalFetchRequestedEventSchema,
  [SystemEvent.AR_DEPOSIT_FILE_SUBMITTED]: ARDepositFileSubmittedEventSchema,
  [SystemEvent.SLACK_WEBHOOK_RECEIVED]: SlackWebhookReceivedEventSchema,
  [SystemEvent.WORKBOOK_RUN_COMPLETED]: WorkbookRunCompletedEventSchema,
} satisfies Record<SystemEvent, z.ZodType>;

export type SystemEventMapType = typeof SystemEventMap;
